import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApi, useAuth0, useBriteVerify, useForm } from 'utils';
import { Dialog, DIALOG_TYPES, Icon, Typography } from 'components/alf-design';
import { datadogLogs } from '@datadog/browser-logs';
import UserProfileField from './UserProfileField';

function validate(values) {
  const errors = {};

  if (!values.username || !/.+@.+\..+/.test(values.username)) {
    errors.username = 'Please provide a valid email address.';
  }

  if (
    values.username &&
    values.usernameConfirm &&
    values.username !== values.usernameConfirm
  ) {
    errors.usernameConfirm = 'The usernames do not match.';
  }

  return errors;
}

const UpdateUsername = ({ show, onClose }) => {
  const { user } = useAuth0();
  const api = useApi();
  const [showSuccess, toggleSuccess] = useState(false);
  const [usernameError, setUernameError] = useState(null);

  const [touched, setTouched] = useState({
    username: false,
    usernameConfirm: false,
  });

  const [blurred, setBlurred] = useState({
    username: false,
    usernameConfirm: false,
  });

  const { handleSubmit, handleChange, values, errors, setErrors } = useForm(
    {
      username: '',
      usernameConfirm: '',
    },
    validate,
    false,
  );

  const handleChangeWithTracking = (e) => {
    const { name, value } = e.target;
    handleChange(e);

    setTouched((prev) => ({ ...prev, [name]: true }));
    setBlurred((prev) => ({ ...prev, [name]: false }));

    if (value === '') {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const markBlurred = (field) => {
    setBlurred((prev) => ({ ...prev, [field]: true }));

    if (values[field]) {
      const validationErrors = validate(values);
      setErrors(validationErrors);
    }
  };

  const usernameStatus = useBriteVerify(
    values.username,
    touched.username,
    blurred.username,
  );

  const isEmpty = (input) =>
    !Object.values(input).some((i) => i !== null && i !== '');

  const newUsernameErrorMessage =
    blurred.username && (usernameStatus === 'invalid' || errors.username)
      ? 'Please provide a valid email address.'
      : null;

  const confirmUsernameErrorMessage =
    blurred.usernameConfirm && errors.usernameConfirm
      ? errors.usernameConfirm
      : null;

  const validationErrorMessages = [
    newUsernameErrorMessage,
    confirmUsernameErrorMessage,
  ].filter(Boolean);

  const sendVerificationEmail = async () => {
    try {
      await api.resendVerificationEmail(values.username);
    } catch (error) {
      let description;
      if (error.response) {
        description = error.response.data.error;
      } else if (error.request) {
        description =
          'Network error. Please check your connection and try again.';
      } else {
        description =
          'There was an error sending the verification email. Please try again later.';
      }
      setUernameError(description);

      datadogLogs.logger.error(`Email Verification - ${values.username}`, {
        error,
      });
    }
  };

  const onSubmit = () => {
    handleSubmit(null, () => {
      api.withAuth
        .post('/identity/change-username', {
          username: values.username,
        })
        .then(async () => {
          sendVerificationEmail();
          handleOnClose();
          toggleSuccess(true);
        })
        .catch((err) => {
          setUernameError(
            "Uh-Oh! We're unable to process your request at this time. Please try again later.",
          );

          datadogLogs.logger.error(`Update Username - ${values.username}`, {
            err,
          });
        });
    });
  };

  const handleOnClose = () => {
    values.username = '';
    values.usernameConfirm = '';
    setUernameError(null);
    setErrors({});
    setTouched({
      username: false,
      usernameConfirm: false,
    });
    setBlurred({
      username: false,
      usernameConfirm: false,
    });
    onClose();
  };

  const isButtonDisabled =
    isEmpty(values) ||
    Object.keys(errors).length > 0 ||
    usernameStatus !== 'valid';

  return (
    <>
      <Dialog
        id="update-username"
        type={DIALOG_TYPES.CONTENT}
        show={show}
        onClose={handleOnClose}
        title="Update Username"
        subtitle="Your username is used to log in to your account."
        primaryButtonText="Update Username"
        onPrimaryClick={isButtonDisabled ? undefined : onSubmit}
        disabledPrimaryBtn={
          isEmpty(values) ||
          Object.keys(errors).length > 0 ||
          usernameStatus !== 'valid'
        }
        ariaLabel="Update username"
        dialogError={usernameError}
      >
        <div className="flex flex-col gap-[8px] mb-[20px]">
          <Typography
            type="h3"
            component="div"
            className="block !text-legacy-textBody"
          >
            Current Username
          </Typography>
          <div className="flex items-center gap-[7.84px]">
            <Icon
              name="MdMailOutline"
              fontSize={16}
              color="#4A5565"
              ariaHidden
              className="ml-[12px] shrink-0"
            />
            <Typography type="caption1" component="span" className="break-all">
              {user?.email || '—'}
            </Typography>
          </div>
        </div>

        <div className="flex flex-col gap-[12px] mb-[22px]">
          <UserProfileField
            label="New Username (Email)"
            name="username"
            value={values.username}
            touched={touched.username}
            errors={blurred.username ? errors.username : ''}
            status={usernameStatus}
            onChange={handleChangeWithTracking}
            onBlur={() => markBlurred('username')}
            successCriteria={blurred.username && usernameStatus === 'valid'}
            errorCriteria={
              blurred.username
                ? usernameStatus === 'invalid'
                  ? 'Please provide a valid email address.'
                  : errors.username
                : ''
            }
            hideErrorMessage
          />

          <UserProfileField
            label="Confirm New Username"
            name="usernameConfirm"
            value={values.usernameConfirm}
            touched={touched.usernameConfirm}
            errors={blurred.usernameConfirm ? errors.usernameConfirm : ''}
            status={
              blurred.usernameConfirm && errors.usernameConfirm
                ? errors.usernameConfirm
                : ''
            }
            onChange={handleChangeWithTracking}
            onBlur={() => markBlurred('usernameConfirm')}
            successCriteria={blurred.usernameConfirm && !errors.usernameConfirm}
            errorCriteria={
              blurred.usernameConfirm && errors.usernameConfirm
                ? errors.usernameConfirm
                : ''
            }
            hideErrorMessage
          />
        </div>

        {validationErrorMessages.length > 0 && (
          <div className="mt-4 flex flex-col gap-[8px]">
            {validationErrorMessages.map((message, index) => (
              <div
                key={`${message}-${index}`}
                className="rounded-[8px] border border-solid border-legacy-error bg-legacy-errorBackground p-[13px] text-left"
                role="alert"
              >
                <Typography
                  type="error"
                  component="p"
                  className="!mb-0 !text-left"
                >
                  {message}
                </Typography>
              </div>
            ))}
          </div>
        )}
      </Dialog>

      <Dialog
        id="update-username-success"
        type={DIALOG_TYPES.CONTENT}
        show={showSuccess}
        onClose={() => toggleSuccess(false)}
        title="Success!"
        primaryButtonText="Close"
        ariaLabel="Username update success"
      >
        <Typography component="p" className="!mb-[16px]">
          Your username has been updated.
        </Typography>
        <Typography type="caption5" component="p" className="!mb-[22px]">
          A verification email has been sent. Please verify your new email
          address before you log in again.
        </Typography>
      </Dialog>
    </>
  );
};

UpdateUsername.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

export default UpdateUsername;
